/**
 * Kit di strumenti (Magazzino): lista kit, editor composizione e assegnazione
 * a negozio/postazione. L'assegnazione crea automaticamente gli item.
 * Permesso: inventory.kits.manage (OPS, AM, HR di default).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Boxes, Plus, Trash2, Pencil, Send, Sparkles, X } from 'lucide-react';
import { kitsApi, stationsApi } from '../lib/api';
import { KitComposeDialog } from './KitComposeDialog';
import { useHasPermission } from '../lib/usePermissions';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { toast } from 'sonner';

const eur = (v) => `€ ${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;

const EMPTY_LINE = { category_id: '', qty: 1, model: '', purchase_price: '', description: '' };

export const KitsPanel = ({ categories, stores, onItemsCreated }) => {
  const canManage = useHasPermission('inventory.kits.manage');
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKit, setEditKit] = useState(null);   // kit esistente da modificare
  const [assignKit, setAssignKit] = useState(null);
  const [compose, setCompose] = useState(null);   // null | {preset: 'base'|null}

  const load = () => {
    kitsApi.list().then(r => setKits(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  async function removeKit(kit) {
    if (!window.confirm(`Eliminare il kit "${kit.name}"? Gli strumenti già creati restano in magazzino.`)) return;
    try {
      await kitsApi.remove(kit.kit_id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  return (
    <div data-testid="kits-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-slate-500">
          Definisci una dotazione standard una sola volta: assegnandola a un negozio o a una postazione,
          gli strumenti vengono creati automaticamente in magazzino con i codici giusti.
        </p>
        {canManage && (
          <div className="flex gap-2 shrink-0 ml-3">
            <Button size="sm" variant="outline" onClick={() => setCompose({ preset: 'base' })}
                    className="rounded-md" data-testid="seed-base-kit-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Kit di base
            </Button>
            <Button size="sm" onClick={() => setCompose({ preset: null })} className="rounded-md" data-testid="new-kit-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuovo kit
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-6">Caricamento…</p>
      ) : kits.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg py-12 text-center" data-testid="kits-empty">
          <Boxes className="w-9 h-9 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600">Nessun kit definito.</p>
          {canManage && <p className="text-xs text-slate-500 mt-1">Usa "Kit di base" per la dotazione standard, o "Nuovo kit" per comporre dal catalogo.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {kits.map(kit => (
            <div key={kit.kit_id} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col"
                 data-testid={`kit-card-${kit.kit_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-heading font-bold text-slate-900 truncate">{kit.name}</h3>
                  {kit.description && <p className="text-xs text-slate-500 mt-0.5">{kit.description}</p>}
                </div>
                {canManage && (
                  <div className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => setEditKit(kit)} className="p-1.5 text-slate-400 hover:text-slate-900"
                            data-testid={`kit-edit-${kit.kit_id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => removeKit(kit)} className="p-1.5 text-slate-400 hover:text-red-600"
                            data-testid={`kit-delete-${kit.kit_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
              <div className="mt-3 space-y-1 flex-1">
                {kit.items.map((l, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-xs">
                    <span className="font-semibold text-slate-900 w-8 shrink-0">{l.qty}×</span>
                    <span className="text-slate-700 truncate">
                      {l.category_name}{l.model ? ` — ${l.model}` : ''}
                    </span>
                    {l.purchase_price != null && l.purchase_price !== '' && (
                      <span className="text-slate-400 ml-auto shrink-0">{eur(l.purchase_price)}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  {kit.total_qty} strumenti · valore {eur(kit.total_value)}
                </span>
                {canManage && (
                  <Button size="sm" variant="outline" onClick={() => setAssignKit(kit)}
                          className="rounded-md h-7 text-xs" data-testid={`kit-assign-${kit.kit_id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Assegna
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {compose && (
        <KitComposeDialog preset={compose.preset} stores={stores}
                          onClose={() => setCompose(null)}
                          onDone={() => { setCompose(null); load(); onItemsCreated?.(); }} />
      )}
      {editKit !== null && (
        <KitEditorDialog kit={editKit} categories={categories}
                         onClose={() => setEditKit(null)}
                         onSaved={() => { setEditKit(null); load(); }} />
      )}
      {assignKit && (
        <KitAssignDialog kit={assignKit} stores={stores}
                         onClose={() => setAssignKit(null)}
                         onAssigned={() => { setAssignKit(null); onItemsCreated?.(); }} />
      )}
    </div>
  );
};

function KitEditorDialog({ kit, categories, onClose, onSaved }) {
  const isNew = !kit.kit_id;
  const [name, setName] = useState(kit.name || '');
  const [description, setDescription] = useState(kit.description || '');
  const [lines, setLines] = useState(
    kit.items?.length
      ? kit.items.map(l => ({ ...EMPTY_LINE, ...l, purchase_price: l.purchase_price ?? '' }))
      : [{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);

  const setLine = (i, patch) => setLines(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));

  async function save() {
    if (name.trim().length < 2) { toast.error('Nome kit troppo corto'); return; }
    const items = lines.filter(l => l.category_id).map(l => ({
      category_id: l.category_id,
      qty: Math.max(1, parseInt(l.qty, 10) || 1),
      model: l.model?.trim() || null,
      purchase_price: l.purchase_price === '' ? null : parseFloat(l.purchase_price),
      description: l.description?.trim() || null,
    }));
    if (!items.length) { toast.error('Aggiungi almeno una riga con categoria'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim() || null, items };
      if (isNew) await kitsApi.create(payload); else await kitsApi.update(kit.kit_id, payload);
      toast.success(isNew ? 'Kit creato' : 'Kit aggiornato');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kit-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Nuovo kit' : `Modifica kit — ${kit.name}`}</DialogTitle>
          <DialogDescription>Composizione della dotazione: categoria, quantità e, se vuoi, modello e prezzo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nome kit *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Es. Kit Barbiere Base" data-testid="kit-name-input" />
            </div>
            <div>
              <Label className="text-xs">Descrizione</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Opzionale" data-testid="kit-description-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Composizione *</Label>
            {lines.map((l, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-2.5 space-y-2" data-testid={`kit-line-${i}`}>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <Select value={l.category_id} onValueChange={(v) => setLine(i, { category_id: v })}>
                      <SelectTrigger className="h-9" data-testid={`kit-line-category-${i}`}><SelectValue placeholder="Categoria *" /></SelectTrigger>
                      <SelectContent>
                        {categories.filter(c => c.active !== false).map(c =>
                          <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input type="number" min="1" max="99" value={l.qty}
                         onChange={e => setLine(i, { qty: e.target.value })}
                         className="w-16 h-9 text-center" data-testid={`kit-line-qty-${i}`} />
                  {lines.length > 1 && (
                    <button type="button" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}
                            className="p-2 text-slate-400 hover:text-red-600" data-testid={`kit-line-remove-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={l.model || ''} onChange={e => setLine(i, { model: e.target.value })}
                         placeholder="Modello (opz.)" className="h-8 text-sm" data-testid={`kit-line-model-${i}`} />
                  <Input type="number" step="0.01" min="0" value={l.purchase_price}
                         onChange={e => setLine(i, { purchase_price: e.target.value })}
                         placeholder="Prezzo € (opz.)" className="h-8 text-sm" data-testid={`kit-line-price-${i}`} />
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setLines(ls => [...ls, { ...EMPTY_LINE }])}
                    className="rounded-md h-8 text-xs" data-testid="kit-add-line-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi riga
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="kit-save-btn">
            {saving ? 'Salvataggio…' : (isNew ? 'Crea kit' : 'Salva modifiche')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KitAssignDialog({ kit, stores, onClose, onAssigned }) {
  const [storeId, setStoreId] = useState('');
  const [stationId, setStationId] = useState('');
  const [stations, setStations] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [newStationName, setNewStationName] = useState('');
  const [creatingStation, setCreatingStation] = useState(false);

  async function createStation() {
    if (!newStationName.trim()) return;
    setCreatingStation(true);
    try {
      const r = await stationsApi.create(storeId, { name: newStationName.trim() });
      setStations(prev => [...prev, r.data]);
      setStationId(r.data.station_id);
      setNewStationName('');
      toast.success(`Postazione "${r.data.name}" creata`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione postazione');
    } finally {
      setCreatingStation(false);
    }
  }

  useEffect(() => {
    setStationId('');
    if (storeId) {
      stationsApi.list(storeId).then(r => setStations(r.data?.stations || [])).catch(() => setStations([]));
    } else {
      setStations([]);
    }
  }, [storeId]);

  const realStores = useMemo(() => stores.filter(s => !s.is_special), [stores]);

  async function assign() {
    if (!storeId) { toast.error('Seleziona un negozio'); return; }
    setAssigning(true);
    try {
      const r = await kitsApi.assign(kit.kit_id, {
        store_id: storeId,
        station_id: stationId || null,
      });
      toast.success(`${r.data.created} strumenti creati in ${r.data.store_name}`);
      onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'assegnazione");
    } finally {
      setAssigning(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="kit-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assegna "{kit.name}"</DialogTitle>
          <DialogDescription>
            Verranno creati {kit.total_qty} nuovi strumenti in magazzino con codici automatici.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Negozio *</Label>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger data-testid="kit-assign-store"><SelectValue placeholder="— seleziona —" /></SelectTrigger>
              <SelectContent>
                {realStores.map(s => <SelectItem key={s.store_id} value={s.store_id}>{s.display_name || s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Postazione (opzionale)</Label>
            <Select value={stationId || 'none'} onValueChange={(v) => setStationId(v === 'none' ? '' : v)} disabled={!storeId}>
              <SelectTrigger data-testid="kit-assign-station"><SelectValue placeholder="— nessuna —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— nessuna (strumenti liberi) —</SelectItem>
                {stations.map(st => <SelectItem key={st.station_id} value={st.station_id}>{st.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {storeId && (
              <div className="flex gap-1.5 mt-1.5">
                <Input value={newStationName} onChange={e => setNewStationName(e.target.value)}
                       placeholder={stations.length === 0 ? 'Nessuna postazione — creane una…' : '+ Nuova postazione…'}
                       className="h-8 text-xs" data-testid="kit-assign-new-station" />
                <Button type="button" size="sm" variant="outline" onClick={createStation}
                        disabled={creatingStation || !newStationName.trim()}
                        className="h-8 text-xs rounded-md shrink-0" data-testid="kit-assign-create-station">
                  {creatingStation ? '…' : 'Crea'}
                </Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={assign} disabled={assigning || !storeId} data-testid="kit-assign-confirm">
            {assigning ? 'Creazione…' : `Crea ${kit.total_qty} strumenti`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
